<script setup lang="ts">
import { useBackToClose } from '@/composables/useBackToClose'
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import {
  Menu, X, ChevronDown, Search, User, LogOut,
  Store, CreditCard, ShieldCheck, Settings,
  Plus, Check, Radio, Gauge,
  Gavel, Landmark, Heart, HelpCircle, LayoutGrid, MapPin, Sparkles, Mic
} from 'lucide-vue-next'
import { auctionService } from '@/services/auctionService'
import { platformCategories } from '@/data/categories'
import { useUserStore } from '@/stores/user'
import { useThemeStore } from '@/stores/theme'
import { useNotificationsStore } from '@/stores/notifications'
import { useI18n } from '@/composables/useI18n'
import { useRouter, useRoute, RouterLink } from 'vue-router'
import { kyrgyzstanRegions } from '@/data/regions'
import type { LocaleKey } from '@/locales'
import Dropdown from '@/components/ui/Dropdown.vue'
import Badge from '@/components/ui/Badge.vue'
import MegaMenu from '@/components/layout/MegaMenu.vue'
import SearchModal from '@/components/layout/SearchModal.vue'
import NotificationBell from '@/components/layout/NotificationBell.vue'
import BrandMark from '@/components/icons/BrandMark.vue'
import { adminPanelUrl } from '@/utils/siteHosts'

const userStore = useUserStore()
const themeStore = useThemeStore()
// Admin > Design > Colours: a white header instead of the brand-colour band.
const headerLight = computed(() => themeStore.theme.headerStyle === 'light')
// 'sun': the band takes the sun colour, with ink text and a brand-blue Sell.
const headerSun = computed(() => themeStore.theme.headerStyle === 'sun')
const bandClass = computed(() => headerLight.value ? 'border-b border-border bg-surface' : headerSun.value ? 'bg-sun' : 'bg-primary')
const bandFg = computed(() => headerLight.value ? 'var(--color-ink)' : headerSun.value ? 'var(--color-on-sun)' : 'var(--color-on-primary)')
const notificationsStore = useNotificationsStore()
const router = useRouter()
const route = useRoute()
// Header Login/Register remember the page the visitor was on, so signing in
// from a lot returns them to that lot instead of the dashboard. Auth pages
// themselves pass their own query through unchanged.
const AUTH_PATHS = ['/login', '/register', '/forgot-password', '/reset-password']
function authLink(path: string) {
  if (AUTH_PATHS.includes(route.path)) return { path, query: route.query }
  if (route.path === '/') return { path }
  return { path, query: { redirect: route.fullPath } }
}
const { t, currentLocale, currentLang, supportedLocales, setLocale } = useI18n()

const mobileMenuOpen = ref(false)
const userMenuOpen = ref(false)
const langMenuOpen = ref(false)
const cityMenuOpen = ref(false)
const categoryMenuOpen = ref(false)
const isSearchModalOpen = ref(false)
const searchStartVoice = ref(false)
const regionSheetOpen = ref(false)

// The header's real height, published as --hdr-h so the drawer (and anything
// else pinned under the header) lines up with it on every breakpoint.
const headerEl = ref<HTMLElement | null>(null)
let headerRO: ResizeObserver | null = null
function syncHeaderHeight() {
  if (!headerEl.value) return
  document.documentElement.style.setProperty('--hdr-h', `${Math.round(headerEl.value.getBoundingClientRect().height)}px`)
}

// Scroll: a soft shadow once the page moves. On phones and tablets the logo
// row slides away while reading downwards and returns on any scroll up; the
// search bar stays pinned the whole time (NN/g "show on scroll up").
const scrolled = ref(false)
const collapsed = ref(false)
let lastY = 0

// Account pages (the dashboard) are about the user's own things, not
// browsing: on phones the search bar and the region/live line give way to a
// search icon, so the page starts right under the logo row.
const compactChrome = computed(() => route.path.startsWith('/dashboard'))

function onScroll() {
  const y = window.scrollY
  scrolled.value = y > 4
  if (compactChrome.value) { collapsed.value = false; lastY = y; return }
  if (mobileMenuOpen.value || isSearchModalOpen.value || regionSheetOpen.value) { lastY = y; return }
  if (y < 96) collapsed.value = false
  else if (y > lastY + 8) collapsed.value = true
  else if (y < lastY - 8) collapsed.value = false
  lastY = y
}

// What is live right now: count and the next lot to close, from the public
// list (the strip and the nav badge stay hidden when nothing is live).
const liveCount = ref(0)
const liveNextEndsAt = ref<string | null>(null)
const liveTitles = ref<string[]>([])
const nowTick = ref(Date.now())
let liveTimer: ReturnType<typeof setInterval> | null = null
let tickTimer: ReturnType<typeof setInterval> | null = null
async function refreshLive() {
  try {
    const res: any = await auctionService.getAuctions({ status: 'active', perPage: 3, sortBy: 'ending_soon' })
    const list: any[] = Array.isArray(res?.data) ? res.data : []
    liveCount.value = Number(res?.meta?.total ?? list.length) || 0
    liveNextEndsAt.value = list[0]?.endsAt || null
    liveTitles.value = list.map(a => String(a?.title || '')).filter(Boolean)
  } catch {
    // Network hiccup: keep whatever was shown last.
  }
}
const liveNextIn = computed(() => {
  if (!liveNextEndsAt.value) return ''
  const mins = Math.max(0, Math.round((new Date(liveNextEndsAt.value).getTime() - nowTick.value) / 60000))
  if (mins < 60) return `${mins} ${t('landing.unitMin')}`
  const h = Math.floor(mins / 60)
  if (h < 24) return `${h} ${t('landing.unitHour')} ${mins % 60} ${t('landing.unitMin')}`
  return `${Math.floor(h / 24)} ${t('landing.unitDay')}`
})

// Search hint: rotates through real live lot titles and typical searches, so
// the field shows what can be found instead of one fixed example.
const PLACEHOLDER_EXAMPLES: Record<string, string[]> = {
  ru: ['Toyota Camry 2015', 'iPhone 15 Pro', 'Квартира в Бишкеке', 'Трактор МТЗ-82', 'Арашан кочкор'],
  ky: ['Toyota Camry 2015', 'iPhone 15 Pro', 'Бишкектен батир', 'МТЗ-82 трактору', 'Арашан кочкор'],
  tr: ['Toyota Camry 2015', 'iPhone 15 Pro', "Bişkek'te daire", 'MTZ-82 traktör', 'Arashan koç'],
}
const placeholders = computed(() => [
  t('nav.searchPlaceholder') || 'MacBook, Camry…',
  ...liveTitles.value.slice(0, 2),
  ...(PLACEHOLDER_EXAMPLES[currentLang.value] || PLACEHOLDER_EXAMPLES.ru),
])
const phIndex = ref(0)
let phTimer: ReturnType<typeof setInterval> | null = null
const currentPlaceholder = computed(() => placeholders.value[phIndex.value % placeholders.value.length])

// Search scope (desktop): follows the category page the visitor is on, and
// can be changed before opening the search (Baymard: "search within").
const scopeCategory = ref('')
watch(() => route.query.category, c => {
  scopeCategory.value = typeof c === 'string' ? c : ''
}, { immediate: true })
const categoryOptions = computed(() => platformCategories.map(c => ({
  slug: c.slug,
  name: (c.name as Record<string, string>)[currentLang.value] || c.name.ru,
})))

// Voice search entry point on phones (the palette runs the recognition).
const voiceSupported = typeof window !== 'undefined'
  && !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition)

// "iTorgo": the leading "i" takes the brand blue on light and sun headers.
const wordmark = computed(() => {
  const text = themeStore.theme.logoText || 'iTorgo'
  return /^i[A-ZА-ЯЁ]/.test(text) ? { lead: text[0], rest: text.slice(1) } : { lead: '', rest: text }
})

// "/" opens search from anywhere that is not a text field (desktop habit).
function onGlobalKey(e: KeyboardEvent) {
  if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return
  const el = e.target as HTMLElement | null
  if (el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))) return
  e.preventDefault()
  openSearch()
}

// Every oblast in @/data/regions is offered, so a buyer in Нарын, Талас or Баткен can
// filter to their own region. The ids are exactly `kyrgyzstanRegions`, which is also what
// LiveAuctionsPage matches `auction.regionId` against — so the id travels in the URL
// and only the label is localized.
const CITY_IDS = kyrgyzstanRegions.map(r => r.id)

// Short city labels where the locale files already have one; otherwise fall back to
// the full localized oblast name from @/data/regions (chuy / issyk_kul have no key yet).
const CITY_KEYS: Record<string, string> = {
  bishkek: 'cities.bishkek',
  osh: 'cities.osh',
  chuy: 'cities.chuy',
  issyk_kul: 'cities.issykKul',
  jalal_abad: 'cities.jalalAbad',
  naryn: 'cities.naryn',
  talas: 'cities.talas',
  batken: 'cities.batken',
}

function cityLabel(id: string): string {
  if (id === 'all') return t('liveAuctionsPage.allKyrgyzstan') || t('common.all')
  const key = CITY_KEYS[id]
  const short = key ? t(key) : ''
  if (short) return short
  const region = kyrgyzstanRegions.find(r => r.id === id)
  return (region?.name as Record<string, string> | undefined)?.[currentLang.value] || region?.name.ru || ''
}

const selectedCityId = ref('bishkek')
// The region travels in the URL (?region=), so a shared or reloaded link keeps it.
watch(() => route.query.region, r => {
  if (typeof r === 'string' && CITY_IDS.includes(r)) selectedCityId.value = r
}, { immediate: true })
const selectedCityLabel = computed(() => cityLabel(selectedCityId.value))
const cities = computed(() => ['all', ...CITY_IDS].map(id => ({ id, name: cityLabel(id) })))

const navLinks = computed(() => [
  { path: '/auctions', label: t('nav.auctions') || 'Каталог', icon: Store },
  { path: '/auctions?status=live', label: t('nav.liveAuctions') || 'Живые торги', icon: Radio, badge: 'LIVE' },
  { path: '/how-it-works', label: t('nav.howItWorks') || 'Как это работает', icon: HelpCircle },
])

const adminUrl = computed(() => adminPanelUrl())

const userMenuItems = computed(() => [
  ...(userStore.isAdmin ? [{
    label: t('nav.adminPanel') || 'Панель управления',
    path: adminUrl.value,
    icon: Gauge,
    badge: 'ADMIN',
    isVerified: true,
    isExternal: true
  }] : []),
  { label: t('dashboard.overview') || 'Обзор', path: '/dashboard/overview', icon: User },
  { label: t('nav.myListings'), path: '/dashboard/listings', icon: Store },
  { label: t('nav.myBids'), path: '/dashboard/bids', icon: Gavel },
  { label: t('dashboard.watchlist') || 'Избранное', path: '/dashboard/watchlist', icon: Heart },
  { label: t('nav.payments'), path: '/dashboard/payments', icon: CreditCard },
  { label: t('nav.payouts'), path: '/dashboard/payouts', icon: Landmark },
  {
    label: t('nav.kycStatus'),
    path: '/dashboard/kyc',
    icon: ShieldCheck,
    badge: userStore.kycStatus === 'verified' ? (t('status.kyc.verified') || 'Подтвержден') : (t('status.kyc.pending') || 'Ожидает'),
    isVerified: userStore.kycStatus === 'verified'
  },
  { label: t('nav.settings'), path: '/dashboard/settings', icon: Settings, divider: true },
])

function selectCity(id: string) {
  selectedCityId.value = id
  cityMenuOpen.value = false
  regionSheetOpen.value = false
  if (id !== 'all') {
    router.push({ path: '/auctions', query: { region: id } })
  } else {
    router.push({ path: '/auctions' })
  }
}

function toggleMobileMenu() {
  mobileMenuOpen.value = !mobileMenuOpen.value
  document.body.style.overflow = mobileMenuOpen.value ? 'hidden' : ''
}

function closeMobileMenu() {
  mobileMenuOpen.value = false
  document.body.style.overflow = ''
}

function closeUserMenu() {
  userMenuOpen.value = false
}

function openSearch(voice = false) {
  searchStartVoice.value = voice
  isSearchModalOpen.value = true
}

async function handleLogout() {
  await userStore.logout()
  router.push('/')
  closeUserMenu()
  closeMobileMenu()
}

// The picker hands back a plain string, and a stale cookie or a hand-edited value can
// reach here too. Anything that is not one of the shipped locale keys is ignored rather
// than forced into setLocale — half-applying an unknown locale is what used to leave the
// UI mixing two languages.
function isLocaleKey(value: string): value is LocaleKey {
  return supportedLocales.some(l => l.code === value)
}

function handleLanguageChange(locale: string) {
  if (isLocaleKey(locale)) setLocale(locale)
  langMenuOpen.value = false
}

// Close the drawer on route change so it never lingers over the next page
const stopRouteWatch = router.afterEach(() => closeMobileMenu())
onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('keydown', onGlobalKey)
  refreshLive()
  liveTimer = setInterval(refreshLive, 60_000)
  tickTimer = setInterval(() => { nowTick.value = Date.now() }, 30_000)
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  if (!reduce) phTimer = setInterval(() => { if (!isSearchModalOpen.value) phIndex.value++ }, 3500)
  if (typeof ResizeObserver !== 'undefined' && headerEl.value) {
    headerRO = new ResizeObserver(syncHeaderHeight)
    headerRO.observe(headerEl.value)
  }
  syncHeaderHeight()
})
onUnmounted(() => {
  stopRouteWatch()
  window.removeEventListener('scroll', onScroll)
  window.removeEventListener('keydown', onGlobalKey)
  if (liveTimer) clearInterval(liveTimer)
  if (tickTimer) clearInterval(tickTimer)
  if (phTimer) clearInterval(phTimer)
  headerRO?.disconnect()
})

watch(() => userStore.isAuthenticated, (val) => {
  if (!val) {
    closeUserMenu()
    notificationsStore.reset()
  }
})

// Phone back button closes the drawer / region sheet instead of leaving the page.
useBackToClose(() => mobileMenuOpen.value, () => closeMobileMenu())
useBackToClose(() => regionSheetOpen.value, () => { regionSheetOpen.value = false })
</script>

<template>
  <header
    ref="headerEl"
    class="fixed top-0 left-1/2 -translate-x-1/2 z-40 w-full max-w-[1440px] border-x border-border/40 transition-[transform,box-shadow] duration-300 ease-out motion-reduce:transition-none"
    :class="[collapsed ? 'max-lg:-translate-y-[2.9375rem]' : '', scrolled ? 'shadow-[0_8px_24px_-14px_rgba(11,37,69,0.4)]' : '']"
  >
    <!-- Brand line: the palette's blue running into the sun -->
    <div aria-hidden="true" class="h-[3px] bg-gradient-to-r from-primary via-primary to-sun" />

    <!-- Main band (Admin > Design: header style 'brand', 'light' or 'sun') -->
    <div :class="bandClass" :style="{ '--hdr-fg': bandFg, color: 'rgb(var(--hdr-fg))' }">
      <div class="mx-auto w-full max-w-[1440px] px-3 sm:px-5 lg:px-6 xl:px-8">
        <div class="flex h-11 items-center gap-2 sm:gap-3 lg:h-16">

          <!-- Brand -->
          <RouterLink to="/" class="group flex min-h-[40px] shrink-0 items-center gap-2 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" :aria-label="themeStore.theme.logoText || 'iTorgo'">
            <img
              v-if="themeStore.theme.logoType === 'image' && themeStore.theme.logoUrl"
              :src="themeStore.theme.logoUrl"
              :alt="themeStore.theme.logoText"
              :style="{ height: (themeStore.theme.logoHeightPx || 36) + 'px' }"
              class="w-auto object-contain"
            />
            <template v-else>
              <BrandMark v-if="themeStore.theme.logoType !== 'text_only'" :ground="headerSun ? 'sun' : undefined" class="h-8 w-8 p-1.5 transition-transform group-hover:scale-105 lg:h-9 lg:w-9" />
              <span class="font-display text-lg font-bold tracking-tight lg:text-xl">
                <span v-if="wordmark.lead" :class="headerLight || headerSun ? 'text-primary' : ''">{{ wordmark.lead }}</span>{{ wordmark.rest }}
              </span>
            </template>
          </RouterLink>

          <!-- Region (desktop) -->
          <Dropdown v-model="cityMenuOpen" trigger="click" placement="bottom" align="start" class="hidden shrink-0 lg:block">
            <template #trigger>
              <button type="button" class="hdr-pill inline-flex" :title="t('ui.header.s4')" :aria-label="`${t('headerBar.region')}: ${selectedCityLabel}`">
                <MapPin class="h-3.5 w-3.5 text-primary" />
                <span class="max-w-[140px] truncate">{{ selectedCityLabel }}</span>
                <ChevronDown class="h-3 w-3 opacity-60 transition-transform" :class="{ 'rotate-180': cityMenuOpen }" />
              </button>
            </template>
            <div class="max-h-[min(70vh,26rem)] w-60 space-y-0.5 overflow-y-auto rounded-2xl border border-border bg-surface p-1.5 shadow-xl">
              <button
                v-for="c in cities"
                :key="c.id"
                class="flex min-h-[40px] w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-xs font-semibold leading-tight transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                :class="c.id === selectedCityId ? 'bg-primary/20 text-text-primary' : 'text-text-secondary hover:bg-accent hover:text-text-primary'"
                @click="selectCity(c.id)"
              >
                <span>{{ c.name }}</span>
                <Check v-if="c.id === selectedCityId" class="h-3.5 w-3.5 shrink-0" />
              </button>
            </div>
          </Dropdown>

          <!-- Search (desktop): scope picker + field + search button in one bar -->
          <div class="hidden min-w-0 flex-1 items-center rounded-full border border-border bg-surface p-1 shadow-[0_2px_10px_-6px_rgba(11,37,69,0.35)] transition focus-within:border-primary/50 hover:border-primary/40 lg:flex">
            <label class="relative hidden shrink-0 xl:block">
              <span class="sr-only">{{ t('headerBar.searchScope') }}</span>
              <select
                v-model="scopeCategory"
                class="h-9 max-w-[11rem] cursor-pointer appearance-none truncate rounded-full bg-accent py-0 pl-3.5 pr-7 text-[12px] font-semibold text-text-primary outline-none transition hover:bg-accent-hover focus-visible:ring-2 focus-visible:ring-primary"
              >
                <option value="">{{ t('nav.allCategories') }}</option>
                <option v-for="c in categoryOptions" :key="c.slug" :value="c.slug">{{ c.name }}</option>
              </select>
              <ChevronDown class="pointer-events-none absolute right-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-text-muted" />
            </label>
            <button
              type="button"
              class="flex h-9 min-w-0 flex-1 items-center gap-2.5 rounded-full px-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              :aria-label="t('common.search')"
              @click="openSearch()"
            >
              <Search class="h-4 w-4 shrink-0 text-text-muted" />
              <span class="relative h-5 min-w-0 flex-1 overflow-hidden">
                <Transition name="ph">
                  <span :key="currentPlaceholder" class="absolute inset-0 truncate text-[13px] leading-5 text-text-secondary">{{ currentPlaceholder }}</span>
                </Transition>
              </span>
              <kbd class="hidden rounded-md border border-border px-1.5 text-[11px] font-semibold text-text-muted 2xl:inline" :title="t('headerBar.shortcutHint')">/</kbd>
              <span class="inline-flex shrink-0 items-center gap-1 rounded-full bg-sun/30 px-2 py-0.5 text-[11px] font-semibold text-onSun">
                <Sparkles class="h-3 w-3" />{{ t('ui.header.s2') }}
              </span>
            </button>
            <button
              type="button"
              class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-onPrimary transition hover:bg-primary-hover active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              :aria-label="t('common.search')"
              @click="openSearch()"
            >
              <Search class="h-4 w-4" />
            </button>
          </div>

          <div class="ml-auto flex shrink-0 items-center gap-1.5 sm:gap-2">
            <!-- Sell (phones use the bottom bar's centre button) -->
            <RouterLink
              to="/sell"
              class="hidden min-h-[40px] items-center gap-2 rounded-full py-1.5 pl-1.5 pr-4 text-xs font-bold shadow-sm transition active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:inline-flex"
              :class="headerLight || headerSun ? 'bg-primary text-onPrimary hover:bg-primary-hover' : 'bg-ink text-white hover:bg-ink-soft'"
            >
              <span class="inline-flex h-7 w-7 items-center justify-center rounded-full bg-sun text-onSun"><Plus class="h-4 w-4 stroke-[2.5]" /></span>
              <span>{{ t('nav.sell') || 'Продать' }}</span>
            </RouterLink>

            <!-- Language -->
            <Dropdown v-model="langMenuOpen" class="hidden sm:block" trigger="click" placement="bottom" align="end">
              <template #trigger>
                <button type="button" class="hdr-pill inline-flex uppercase" :title="t('nav.language')" :aria-label="t('nav.language')">
                  <span>{{ currentLocale.code }}</span>
                  <ChevronDown class="h-3 w-3 opacity-60 transition-transform" :class="{ 'rotate-180': langMenuOpen }" />
                </button>
              </template>
              <div class="w-48 space-y-0.5 rounded-2xl border border-border bg-surface p-1.5 shadow-xl">
                <button
                  v-for="loc in supportedLocales"
                  :key="loc.code"
                  class="flex min-h-[40px] w-full items-center justify-between rounded-lg px-3 py-2 text-left text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  :class="loc.code === currentLocale.code ? 'bg-primary/20 text-text-primary' : 'text-text-secondary hover:bg-accent hover:text-text-primary'"
                  @click="handleLanguageChange(loc.code)"
                >
                  <span class="flex items-center gap-2.5">{{ loc.nativeName }}</span>
                  <Check v-if="loc.code === currentLocale.code" class="h-3.5 w-3.5 shrink-0" />
                </button>
              </div>
            </Dropdown>

            <!-- Search icon where the phone search bar is hidden (account pages) -->
            <button
              v-if="compactChrome"
              type="button"
              class="hdr-pill inline-flex !h-10 !w-10 justify-center !p-0 lg:hidden"
              :aria-label="t('common.search')"
              @click="openSearch()"
            >
              <Search class="h-5 w-5" />
            </button>

            <NotificationBell v-if="userStore.isAuthenticated" />

            <!-- Account -->
            <Dropdown v-if="userStore.isAuthenticated" v-model="userMenuOpen" trigger="click" placement="bottom" align="end">
              <template #trigger>
                <button type="button" class="hdr-pill inline-flex !py-1 !pl-1 !pr-2.5">
                  <span class="flex h-7 w-7 items-center justify-center rounded-full bg-ink text-xs font-bold text-sun">{{ userStore.fullName?.charAt(0) || 'U' }}</span>
                  <span class="hidden max-w-[110px] truncate text-xs font-semibold text-[rgb(var(--hdr-fg))] sm:inline">{{ userStore.fullName }}</span>
                  <ChevronDown class="hidden h-3 w-3 opacity-60 transition-transform sm:block" :class="{ 'rotate-180': userMenuOpen }" />
                </button>
              </template>
              <div class="w-64 rounded-2xl border border-border bg-surface p-1.5 shadow-xl">
                <div class="mb-1.5 rounded-xl bg-background px-3.5 py-3">
                  <p class="truncate text-xs font-bold text-text-primary">{{ userStore.fullName }}</p>
                  <p class="truncate text-[11px] text-text-muted">{{ userStore.user?.email }}</p>
                  <!-- Payout balance only means something to a seller -->
                  <div v-if="userStore.isSeller" class="mt-2.5 flex items-center justify-between border-t border-border pt-2 text-xs">
                    <span class="text-text-secondary">{{ t('nav.balance') || 'Баланс' }}</span>
                    <span class="font-display font-bold tabular text-text-primary">{{ userStore.formattedBalance }}</span>
                  </div>
                </div>
                <div class="space-y-0.5">
                  <component
                    :is="item.isExternal ? 'a' : RouterLink"
                    v-for="item in userMenuItems"
                    :key="item.label"
                    :to="!item.isExternal ? item.path : undefined"
                    :href="item.isExternal ? item.path : undefined"
                    :target="item.isExternal ? '_blank' : undefined"
                    :rel="item.isExternal ? 'noopener noreferrer' : undefined"
                    class="flex min-h-[40px] items-center gap-2.5 rounded-lg px-3 py-2 text-xs font-semibold text-text-secondary transition hover:bg-accent hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    @click="closeUserMenu"
                  >
                    <component :is="item.icon" class="h-4 w-4 shrink-0 text-text-muted" />
                    <span class="truncate">{{ item.label }}</span>
                    <Badge v-if="item.badge" :variant="(item as any).isVerified ? 'success' : 'warning'" size="sm" class="ml-auto">{{ item.badge }}</Badge>
                  </component>
                </div>
                <div class="my-1 border-t border-border" />
                <button class="flex min-h-[40px] w-full items-center gap-2.5 rounded-lg px-3 py-2 text-xs font-semibold text-error transition hover:bg-error/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" @click="handleLogout">
                  <LogOut class="h-4 w-4" />
                  <span>{{ t('nav.logout') || 'Выйти' }}</span>
                </button>
              </div>
            </Dropdown>
            <RouterLink
              v-else
              :to="authLink('/login')"
              class="hdr-pill hidden sm:inline-flex"
            >
              <User class="h-3.5 w-3.5" />
              <span>{{ t('nav.login') || 'Войти' }}</span>
            </RouterLink>

            <!-- Menu (phones and tablets) -->
            <button
              class="hdr-pill inline-flex !h-10 !w-10 justify-center !p-0 lg:hidden"
              :aria-label="t('common.menu') || t('nav.navigation')"
              :aria-expanded="mobileMenuOpen"
              @click="toggleMobileMenu"
            >
              <Menu v-if="!mobileMenuOpen" class="h-5 w-5" />
              <X v-else class="h-5 w-5" />
            </button>
          </div>
        </div>

        <!-- Search (phones and tablets): stays pinned while the logo row hides -->
        <div v-if="!compactChrome" class="pb-1.5 lg:hidden">
          <div class="flex h-9 items-center rounded-full border border-border bg-surface pl-3.5 pr-1 shadow-[0_2px_10px_-6px_rgba(11,37,69,0.35)]">
            <button type="button" class="flex h-full min-w-0 flex-1 items-center gap-2.5 text-left focus-visible:outline-none" :aria-label="t('common.search')" @click="openSearch()">
              <Search class="h-4 w-4 shrink-0 text-text-muted" />
              <span class="relative h-5 min-w-0 flex-1 overflow-hidden">
                <Transition name="ph">
                  <span :key="currentPlaceholder" class="absolute inset-0 truncate text-[13px] leading-5 text-text-secondary">{{ currentPlaceholder }}</span>
                </Transition>
              </span>
            </button>
            <button
              v-if="voiceSupported"
              type="button"
              class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-primary transition hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              :aria-label="t('headerBar.voiceSearch')"
              @click="openSearch(true)"
            >
              <Mic class="h-4 w-4" />
            </button>
            <button type="button" class="inline-flex h-7 shrink-0 items-center gap-1 rounded-full bg-sun/30 px-2 text-[11px] font-semibold text-onSun focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" :aria-label="t('ui.header.s2')" @click="openSearch()">
              <Sparkles class="h-3 w-3" />AI
            </button>
          </div>
        </div>
      </div>

      <!-- Context line (phones and tablets): region + what is live right now -->
      <div v-if="!compactChrome" class="overflow-hidden transition-[max-height,opacity] duration-300 motion-reduce:transition-none lg:hidden" :class="collapsed ? 'max-h-0 opacity-0' : 'max-h-8 opacity-100'">
        <div class="flex h-[22px] items-center justify-between gap-3 px-3 text-[11px] sm:px-5">
          <button type="button" class="inline-flex min-w-0 items-center gap-1 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" :aria-label="`${t('headerBar.region')}: ${selectedCityLabel}. ${t('headerBar.change')}`" @click="regionSheetOpen = true">
            <MapPin class="h-3.5 w-3.5 shrink-0 text-primary" />
            <span class="truncate">{{ selectedCityLabel }}</span>
            <span class="shrink-0 font-medium text-primary">· {{ t('headerBar.change') }}</span>
          </button>
          <RouterLink
            v-if="liveCount > 0"
            to="/auctions?status=live"
            class="inline-flex min-w-0 items-center gap-1.5 truncate font-medium text-success focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <span class="relative inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-success text-success live-dot" />
            <span class="truncate">{{ t('headerBar.liveNow', { n: liveCount }) }}<template v-if="liveNextIn"> · {{ t('headerBar.nextEnds', { time: liveNextIn }) }}</template></span>
          </RouterLink>
        </div>
      </div>
    </div>

    <!-- Secondary nav (desktop) -->
    <div class="hidden border-b border-border bg-surface lg:block">
      <div class="mx-auto flex h-10 w-full max-w-[1440px] items-center gap-1 px-6 xl:px-8">
        <Dropdown v-model="categoryMenuOpen" trigger="hover" placement="bottom" align="start">
          <template #trigger>
            <button type="button" class="group inline-flex items-center gap-2 rounded-full bg-primary/10 px-3.5 py-1.5 text-[13px] font-semibold text-primary-hover transition hover:bg-primary/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
              <LayoutGrid class="h-4 w-4" />
              <span>{{ t('nav.allCategories') || 'Все категории' }}</span>
              <ChevronDown class="h-3.5 w-3.5 transition-transform" :class="{ 'rotate-180': categoryMenuOpen }" />
            </button>
          </template>
          <div class="overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl">
            <MegaMenu @navigate="categoryMenuOpen = false" />
          </div>
        </Dropdown>

        <span class="mx-2 h-4 w-px bg-border" />

        <nav class="flex h-full items-center gap-0.5">
          <RouterLink
            v-for="link in navLinks"
            :key="link.path"
            :to="link.path"
            class="relative inline-flex h-full items-center gap-1.5 px-3 text-[13px] font-medium text-text-secondary transition hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
            exact-active-class="!text-text-primary !font-semibold after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:rounded-full after:bg-primary"
          >
            <span>{{ link.label }}</span>
            <span v-if="link.badge && liveCount > 0" class="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-success/10 px-1.5 text-[11px] font-bold tabular text-success">{{ liveCount }}</span>
            <span v-else-if="link.badge" class="relative inline-flex h-2 w-2 rounded-full bg-success text-success live-dot" :title="link.label" />
          </RouterLink>
        </nav>

        <span class="ml-auto inline-flex items-center gap-1.5 text-[12px] text-text-muted">
          <ShieldCheck class="h-3.5 w-3.5 text-primary" />
          <span>{{ t('home.trustEscrowTitle') }}</span>
        </span>
      </div>
    </div>

    <SearchModal v-model="isSearchModalOpen" :category="scopeCategory" :start-voice="searchStartVoice" />
  </header>

  <!-- Region sheet (phones and tablets) -->
  <Teleport to="body">
    <Transition name="sidebar">
      <div v-if="regionSheetOpen" class="fixed inset-0 z-[60] flex items-end bg-ink/50 backdrop-blur-sm lg:hidden" @click.self="regionSheetOpen = false">
        <div class="max-h-[75vh] w-full overflow-y-auto rounded-t-[1.5rem] bg-surface p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-2xl" role="dialog" aria-modal="true" :aria-label="t('headerBar.regionTitle')">
          <div class="mx-auto mb-3 h-1.5 w-12 rounded-full bg-border" />
          <div class="mb-2 flex items-center justify-between">
            <h2 class="font-display text-base font-bold text-text-primary">{{ t('headerBar.regionTitle') }}</h2>
            <button type="button" class="inline-flex h-10 w-10 items-center justify-center rounded-full text-text-muted hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" :aria-label="t('common.close')" @click="regionSheetOpen = false"><X class="h-5 w-5" /></button>
          </div>
          <div class="grid grid-cols-2 gap-2">
            <button
              v-for="c in cities"
              :key="c.id"
              type="button"
              class="flex min-h-[44px] items-center justify-between gap-2 rounded-xl border px-3 py-2 text-left text-[13px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              :class="c.id === selectedCityId ? 'border-primary bg-primary/10 text-text-primary' : 'border-border text-text-secondary hover:bg-accent'"
              @click="selectCity(c.id)"
            >
              <span class="truncate">{{ c.name }}</span>
              <Check v-if="c.id === selectedCityId" class="h-4 w-4 shrink-0 text-primary" />
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- Mobile drawer -->
  <Transition name="sidebar">
    <div v-if="mobileMenuOpen" class="fixed inset-x-0 bottom-0 z-50 bg-ink/50 backdrop-blur-sm lg:hidden" :style="{ top: 'var(--hdr-h, 7rem)' }" @click="closeMobileMenu">
      <div class="flex h-full w-4/5 max-w-sm flex-col justify-between overflow-y-auto bg-surface p-5 shadow-2xl" @click.stop>
        <div class="space-y-4">
          <RouterLink to="/sell" class="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 text-sm font-bold text-onPrimary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface" @click="closeMobileMenu">
            <Plus class="h-4 w-4 stroke-[2.5]" />
            <span>{{ t('nav.sell') || 'Продать лот' }}</span>
          </RouterLink>

          <div v-if="!userStore.isAuthenticated" class="grid grid-cols-2 gap-2">
            <RouterLink :to="authLink('/login')" class="inline-flex min-h-[40px] items-center justify-center rounded-xl border border-border px-2 py-2.5 text-center text-xs font-semibold text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" @click="closeMobileMenu">{{ t('nav.login') || 'Войти' }}</RouterLink>
            <RouterLink :to="authLink('/register')" class="inline-flex min-h-[40px] items-center justify-center rounded-xl bg-primary px-2 py-2.5 text-center text-xs font-bold text-onPrimary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface" @click="closeMobileMenu">{{ t('nav.register') || 'Регистрация' }}</RouterLink>
          </div>
          <RouterLink v-else to="/dashboard/overview" class="flex min-h-[40px] items-center justify-between rounded-xl bg-background p-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" @click="closeMobileMenu">
            <div class="flex min-w-0 items-center gap-2.5">
              <span class="flex h-8 w-8 items-center justify-center rounded-lg bg-ink text-xs font-bold text-primary">{{ userStore.fullName?.charAt(0) || 'U' }}</span>
              <div class="min-w-0">
                <p class="truncate text-xs font-bold text-text-primary">{{ userStore.fullName }}</p>
                <p v-if="userStore.isSeller" class="truncate text-[11px] tabular text-text-muted">{{ t('nav.balance') }}: {{ userStore.formattedBalance }}</p>
                <p v-else class="truncate text-[11px] text-text-muted">{{ userStore.user?.email }}</p>
              </div>
            </div>
            <ChevronDown class="h-4 w-4 -rotate-90 text-text-muted" />
          </RouterLink>

          <nav class="space-y-0.5 pt-1">
            <RouterLink to="/categories" class="flex min-h-[40px] items-center justify-between rounded-lg px-3 py-2.5 text-sm font-semibold text-text-primary hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" @click="closeMobileMenu">
              <span class="flex items-center gap-3"><LayoutGrid class="h-4 w-4 text-primary-hover" />{{ t('nav.allCategories') || 'Все категории' }}</span>
              <ChevronDown class="h-3.5 w-3.5 -rotate-90 text-text-muted" />
            </RouterLink>
            <RouterLink v-for="link in navLinks" :key="link.path" :to="link.path" class="flex min-h-[40px] items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium text-text-secondary hover:bg-accent hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" @click="closeMobileMenu">
              <span class="flex items-center gap-3"><component :is="link.icon" class="h-4 w-4 text-text-muted" />{{ link.label }}</span>
              <span v-if="link.badge" class="relative inline-flex h-2 w-2 rounded-full bg-success text-success live-dot" />
            </RouterLink>
            <button type="button" class="flex min-h-[40px] w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium leading-tight text-text-secondary hover:bg-accent hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:hidden" :aria-label="t('ui.header.s4')" :aria-expanded="cityMenuOpen" @click="cityMenuOpen = !cityMenuOpen">
              <MapPin class="h-4 w-4 shrink-0 text-text-muted" />{{ selectedCityLabel }}
            </button>
            <div v-if="cityMenuOpen" class="grid grid-cols-2 gap-1.5 px-3 pb-2 md:hidden">
              <button v-for="c in cities" :key="c.id" type="button" class="flex min-h-[40px] items-center rounded-lg border px-2 py-1.5 text-left text-xs font-medium leading-tight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" :class="c.id === selectedCityId ? 'border-primary bg-primary/15 text-text-primary' : 'border-border text-text-secondary'" @click="selectCity(c.id); closeMobileMenu()">
                {{ c.name }}
              </button>
            </div>
          </nav>

          <div class="border-t border-border pt-4">
            <p class="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-text-muted">{{ t('nav.language') || 'Язык платформы' }}</p>
            <div class="grid grid-cols-1 gap-1.5">
              <button
                v-for="loc in supportedLocales"
                :key="loc.code"
                type="button"
                class="flex min-h-[40px] w-full items-center justify-between rounded-lg border px-3 py-2 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                :class="loc.code === currentLocale.code ? 'border-primary bg-primary/15 text-text-primary' : 'border-border text-text-secondary hover:bg-accent'"
                @click="handleLanguageChange(loc.code)"
              >
                <span class="flex items-center gap-2.5">
                  <span>{{ loc.nativeName }}</span>
                </span>
                <Check v-if="loc.code === currentLocale.code" class="h-3.5 w-3.5 shrink-0" />
              </button>
            </div>
          </div>
        </div>

        <p class="border-t border-border pt-4 text-center text-[11px] text-text-muted">{{ t('ui.header.s3') }}</p>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* One pill for every secondary header control; its colours follow the band
   (--hdr-fg), so it works on the light, brand and sun header styles. */
.hdr-pill {
  min-height: 40px;
  align-items: center;
  gap: 0.375rem;
  border-radius: 9999px;
  border: 1px solid rgb(var(--hdr-fg) / 0.14);
  background: rgb(var(--hdr-fg) / 0.02);
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: rgb(var(--hdr-fg));
  transition: background-color 0.15s ease, border-color 0.15s ease;
}
.hdr-pill:hover {
  background: rgb(var(--hdr-fg) / 0.07);
  border-color: rgb(var(--hdr-fg) / 0.22);
}
.hdr-pill:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px rgb(var(--color-surface)), 0 0 0 4px rgb(var(--color-primary));
}

/* Rotating search examples slide up softly; reduced motion: no rotation (see script). */
.ph-enter-active,
.ph-leave-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}
.ph-enter-from {
  opacity: 0;
  transform: translateY(0.6rem);
}
.ph-leave-to {
  opacity: 0;
  transform: translateY(-0.6rem);
}

.sidebar-enter-active,
.sidebar-leave-active {
  transition: opacity 0.2s ease;
}
.sidebar-enter-from,
.sidebar-leave-to {
  opacity: 0;
}
</style>
